// client/src/pages/ExtensionDetail.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Crown, TriangleAlert } from 'lucide-react';
import { ExtensionJob } from '../types/portal';
import { fetchExtensionJobs } from '../services/portalData';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function coerceObject(value: unknown): Record<string, string> {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) return value as Record<string, string>;
  if (typeof value === 'string') {
    const s = value.trim();
    if (!s) return {};
    try {
      const parsed = JSON.parse(s);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed as Record<string, string>;
    } catch { /* fall through */ }
  }
  return {};
}

function isVip(job: ExtensionJob): boolean {
  return (job.isVIP || '').toString().toUpperCase() === 'TRUE';
}

// ─── Extension notes jurisdiction config ─────────────────────────────────────

const EXT_JURISDICTIONS = [
  {
    label: 'Federal',
    color: '#7eb8f7',
    bg: 'rgba(126,184,247,0.07)',
    borderColor: 'rgba(126,184,247,0.25)',
    fields: {
      'Filing Method':  'Federal Extension Filing Method',
      'Pay Method':     'Federal Extension Pay Method',
      'Amount':         'Federal Extension Payment Amount',
      'Instructions':   'Federal Extension Payment Instructions ',
      'Notes':          'Federal Notes:',
    },
  },
  {
    label: 'Alabama',
    color: '#a78bfa',
    bg: 'rgba(167,139,250,0.07)',
    borderColor: 'rgba(167,139,250,0.25)',
    fields: {
      'Filing Method':  'Alabama Extension Filing Method',
      'Pay Method':     'Alabama Extension Pay Method',
      'Amount':         'Alabama Extension Payment Amount',
      'Instructions':   'Alabama Extension Payment Instructions ',
      'Notes':          'Alabama Notes:',
    },
  },
  {
    label: 'AL PPT',
    color: '#6ee7b7',
    bg: 'rgba(110,231,183,0.07)',
    borderColor: 'rgba(110,231,183,0.25)',
    fields: {
      'Filing Method':  'AL PPT Extension Filing Method',
      'Pay Method':     'AL PPT Extension Pay Method',
      'Amount':         'AL PPT Extension Payment Amount',
      'Instructions':   'AL PPT Extension Payment Instructions ',
      'Notes':          'AL PPT Notes:',
    },
  },
  {
    label: 'Mississippi',
    color: '#fbbf24',
    bg: 'rgba(251,191,36,0.07)',
    borderColor: 'rgba(251,191,36,0.25)',
    fields: {
      'Filing Method':  'Mississippi Extension Filing Method',
      'Pay Method':     'Mississippi Extension Pay Method',
      'Amount':         'Mississippi Extension Payment Amount',
      'Instructions':   'Mississippi Extension Payment Instructions ',
      'Notes':          'Mississippi Notes:',
    },
  },
  {
    label: 'Mississippi Franchise',
    color: '#f59e0b',
    bg: 'rgba(245,158,11,0.07)',
    borderColor: 'rgba(245,158,11,0.25)',
    fields: {
      'Filing Method':  'Mississippi Franchise Tax Filing Method',
      'Pay Method':     'Mississippi Franchise Tax Pay Method',
      'Amount':         'Mississippi Franchise Extension Payment Amount',
      'Instructions':   'Mississippi Franchise Extension Payment Instructions ',
      'Notes':          'Mississippi Franchise Notes:',
    },
  },
] as const;

const OTHER_FIELDS: Record<string, string> = {
  'Other States Extension Notes': 'Other States Extension Notes',
  'Other Extension Instructions':  'Other Extension Instructions',
};

// All known keys — anything not in here gets rendered as a generic extra row
const ALL_KNOWN_KEYS = new Set<string>([
  ...EXT_JURISDICTIONS.flatMap((j) => Object.values(j.fields)),
  ...Object.keys(OTHER_FIELDS),
]);

type LocationState = { from?: string; job?: ExtensionJob };

// ─── Main component ───────────────────────────────────────────────────────────

export default function ExtensionDetail() {
  const { returnId: returnIdParam } = useParams();
  const decodedReturnId = useMemo(() => {
    try { return decodeURIComponent(returnIdParam || ''); }
    catch { return returnIdParam || ''; }
  }, [returnIdParam]);

  const navigate  = useNavigate();
  const location  = useLocation();
  const state     = (location.state || {}) as LocationState;

  const [job, setJob]               = useState<ExtensionJob | null>(state.job || null);
  const [jobLoading, setJobLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function loadJob() {
      if (job || !decodedReturnId) return;
      setJobLoading(true);
      try {
        const jobs = await fetchExtensionJobs();
        if (cancelled) return;
        setJob(jobs.find((j) => (j.returnId || '') === decodedReturnId) || null);
      } finally {
        if (!cancelled) setJobLoading(false);
      }
    }
    loadJob();
    return () => { cancelled = true; };
  }, [decodedReturnId, job]);

  const backTo = state.from || '/portal/extensions';

  // ── Core job fields ────────────────────────────────────────────────────────
  const fields = useMemo(() => {
    if (!job) return [] as Array<[string, string]>;
    const emails = [
      job.PrimaryEmail ? `${job.PrimaryEmail} (primary)` : null,
      job.SpouseEmail  ? `${job.SpouseEmail} (spouse)`   : null,
    ].filter(Boolean).join(' • ');
    const addrParts = [job.Street, job.City, job.State, job.ZIP].filter(Boolean);
    const address   = addrParts.length ? addrParts.join(', ') : '—';
    const phones    = [
      job.Phone ? `${job.Phone} (office)` : null,
      job.Cell  ? `${job.Cell} (cell)`    : null,
    ].filter(Boolean).join(' • ');
    return [
      ['Client Partner',    job.ClientPartner || '—'],
      ['Client Manager',    job.ClientManager || '—'],
      ['Job Partner',       job.JobPartner    || '—'],
      ['Job Manager',       job.JobManager    || '—'],
      ['Current Staff',     job.CurrentStaff  || '—'],
      ['Office',            job.ClientOffice  || '—'],
      ['Entity Type',       job.EntityType    || '—'],
      ['Ext Code',          job.ExtensionCode || '—'],
      ['Work Status',       job.JobWorkStatus || '—'],
      ['Fed Ext Status',    job.ExtensionStatusFed    || '—'],
      ['Fed Jurisdiction',  job.FedJurisdictionStatus || '—'],
      ['Email',             emails  || '—'],
      ['Address',           address],
      ['Phone',             phones  || '—'],
    ] as Array<[string, string]>;
  }, [job]);

  // ── Parse extension_notes into jurisdiction groups ─────────────────────────
  const extNotes = useMemo(() => {
    const raw = coerceObject(job?.extension_notes);

    // Build jurisdiction data
    const jurisdictions = EXT_JURISDICTIONS.map((juris) => {
      const values: Record<string, string> = {};
      for (const [shortLabel, rawKey] of Object.entries(juris.fields)) {
        values[shortLabel] = raw[rawKey]?.trim() || '';
      }
      const hasAny = Object.values(values).some(Boolean);
      return { ...juris, values, hasAny };
    });

    // Other fields
    const otherValues: Record<string, string> = {};
    for (const key of Object.keys(OTHER_FIELDS)) {
      otherValues[key] = raw[key]?.trim() || '';
    }
    const otherHasAny = Object.values(otherValues).some(Boolean);

    // Extra keys not in the known set
    const extra = Object.entries(raw)
      .filter(([k]) => !ALL_KNOWN_KEYS.has(k.trim()))
      .map(([k, v]) => [k.trim(), v?.trim() || ''] as [string, string])
      .filter(([, v]) => v);

    return { jurisdictions, otherValues, otherHasAny, extra };
  }, [job]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="detail-grid">
      <div className="row" style={{ justifyContent: 'flex-start' }}>
        <button className="btn" type="button" onClick={() => { if (window.history.length > 1) navigate(-1); else navigate(backTo); }}>
          ← Back
        </button>
        <span className="badge">returnId: {decodedReturnId || '—'}</span>
      </div>

      {/* ── Job Card ──────────────────────────────────────────────────────── */}
      <section className="card">
        <div className="hd">
          <div className="row">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ fontWeight: 760, display: 'flex', alignItems: 'center', gap: 8 }}>
                {job?.jobLink ? (
                  <a href={job.jobLink} target="_blank" rel="noopener noreferrer"
                    style={{ textDecoration: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, padding: '3px 10px', borderRadius: 6, fontSize: 15, fontWeight: 700, backgroundColor: '#1a3a5c', border: '1px solid #7eb8f755', color: '#7eb8f7', transition: 'border-color 0.15s, background-color 0.15s', width: '100%', boxSizing: 'border-box' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#1e4570'; (e.currentTarget as HTMLAnchorElement).style.borderColor = '#7eb8f7'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#1a3a5c'; (e.currentTarget as HTMLAnchorElement).style.borderColor = '#7eb8f755'; }}>
                    <span>{job.Job_Name || (jobLoading ? 'Loading…' : 'Job not found')}</span>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.5, flexShrink: 0 }}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                  </a>
                ) : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 6, fontSize: 15, fontWeight: 700, width: '100%', boxSizing: 'border-box' }}>
                    {job?.Job_Name || (jobLoading ? 'Loading…' : 'Job not found')}
                  </span>
                )}
                {job && isVip(job) && (
                  <span title="Client >$20k in Past Year"
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '50%', backgroundColor: '#0f2444', border: '1px solid #7eb8f7', color: '#7eb8f7', flexShrink: 0, cursor: 'default' }}>
                    <Crown size={13} />
                  </span>
                )}
              </div>
              <div style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                {job?.clientLink ? (
                  <a href={job.clientLink} target="_blank" rel="noopener noreferrer"
                    style={{ textDecoration: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, padding: '3px 10px', borderRadius: 6, fontSize: 15, fontWeight: 700, backgroundColor: '#2d1f5e', border: '1px solid #a78bfa55', color: '#a78bfa', transition: 'border-color 0.15s, background-color 0.15s', width: '100%', boxSizing: 'border-box' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#3a2870'; (e.currentTarget as HTMLAnchorElement).style.borderColor = '#a78bfa'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#2d1f5e'; (e.currentTarget as HTMLAnchorElement).style.borderColor = '#a78bfa55'; }}>
                    <span>{job?.ClientCode || '—'} · {job?.ClientName || '—'}</span>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.5, flexShrink: 0 }}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                  </a>
                ) : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 6, fontSize: 15, fontWeight: 700, backgroundColor: '#2d1f5e', border: '1px solid #a78bfa55', color: '#a78bfa', width: '100%', boxSizing: 'border-box' }}>
                    {job?.ClientCode || '—'} · {job?.ClientName || '—'}
                  </span>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end' }}>
              <span className="pill">Due: {job?.CurrentDueDate ? new Date(job.CurrentDueDate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '—'}</span>
              <span className="pill">Work: {job?.JobWorkStatus || '—'}</span>
              {job?.ExtensionStatusFed && <span className="pill">Fed Ext: {job.ExtensionStatusFed}</span>}
              {job?.FedJurisdictionStatus && <span className="pill">Jurisdiction: {job.FedJurisdictionStatus}</span>}
            </div>
          </div>
        </div>

        <div className="bd">
          <div className="kv">
            {fields.map(([k, v]) => (
              <div key={k} className="field">
                <div className="k">{k}</div>
                <div className="v">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Extension Notes ────────────────────────────────────────────────── */}
      {job && (
        <section className="card">
          <div className="hd">
            <div className="row">
              <div style={{ fontWeight: 760 }}>Extension Instructions</div>
              <div className="muted" style={{ fontSize: 13 }}>
                Per-jurisdiction filing method, payment, and notes
              </div>
            </div>
          </div>
          <div className="bd" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

            {extNotes.jurisdictions.map((juris) => (
              <div key={juris.label} style={{
                borderRadius: 8,
                border: `1px solid ${juris.hasAny ? juris.borderColor : 'var(--border)'}`,
                overflow: 'hidden',
                opacity: juris.hasAny ? 1 : 0.45,
              }}>
                {/* Jurisdiction header */}
                <div style={{
                  padding: '6px 14px',
                  background: juris.hasAny ? juris.bg : 'transparent',
                  borderBottom: `1px solid ${juris.hasAny ? juris.borderColor : 'var(--border)'}`,
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: juris.hasAny ? juris.color : 'var(--muted)' }}>
                    {juris.label}
                  </span>
                  {!juris.hasAny && (
                    <span className="tiny muted">— no data</span>
                  )}
                </div>

                {/* Fields grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(5, 1fr)',
                  gap: 0,
                }}>
                  {Object.entries(juris.values).map(([shortLabel, value], idx) => (
                    <div key={shortLabel} style={{
                      padding: '8px 12px',
                      borderRight: idx < Object.keys(juris.values).length - 1 ? '1px solid var(--border)' : 'none',
                    }}>
                      <div className="k" style={{ color: juris.hasAny && value ? juris.color : undefined, marginBottom: 4 }}>
                        {shortLabel}
                      </div>
                      <div className="v" style={{ wordBreak: 'break-word' }}>
                        {value || <span className="muted" style={{ opacity: 0.4 }}>—</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Other States */}
            <div style={{
              borderRadius: 8,
              border: `1px solid ${extNotes.otherHasAny ? 'rgba(148,163,184,0.3)' : 'var(--border)'}`,
              overflow: 'hidden',
              opacity: extNotes.otherHasAny ? 1 : 0.45,
            }}>
              <div style={{
                padding: '6px 14px',
                background: extNotes.otherHasAny ? 'rgba(148,163,184,0.07)' : 'transparent',
                borderBottom: `1px solid ${extNotes.otherHasAny ? 'rgba(148,163,184,0.3)' : 'var(--border)'}`,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <span style={{ fontWeight: 700, fontSize: 13, color: extNotes.otherHasAny ? '#94a3b8' : 'var(--muted)' }}>
                  Other States
                </span>
                {!extNotes.otherHasAny && <span className="tiny muted">— no data</span>}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                {Object.entries(extNotes.otherValues).map(([label, value], idx) => (
                  <div key={label} style={{
                    padding: '8px 12px',
                    borderRight: idx === 0 ? '1px solid var(--border)' : 'none',
                  }}>
                    <div className="k" style={{ marginBottom: 4 }}>{label}</div>
                    <div className="v" style={{ wordBreak: 'break-word' }}>
                      {value || <span className="muted" style={{ opacity: 0.4 }}>—</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Any unexpected extra fields */}
            {extNotes.extra.length > 0 && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 10,
              }}>
                {extNotes.extra.map(([label, value]) => (
                  <div key={label} className="field" style={{ borderRadius: 6, padding: '8px 12px' }}>
                    <div className="k">{label}</div>
                    <div className="v">{value}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Empty state */}
            {!extNotes.jurisdictions.some((j) => j.hasAny) && !extNotes.otherHasAny && extNotes.extra.length === 0 && (
              <div className="muted" style={{ padding: '16px 0', fontSize: 13 }}>
                No extension instructions have been entered for this return yet.
              </div>
            )}
          </div>
        </section>
      )}

      {/* If no job loaded */}
      {!job && !jobLoading && (
        <section className="card">
          <div className="bd">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderRadius: 8, backgroundColor: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.35)', color: '#fbbf24', fontSize: 14 }}>
              <TriangleAlert size={16} style={{ flexShrink: 0 }} />
              <span>Extension return not found for returnId <strong>{decodedReturnId}</strong>.</span>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
